import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { autorun, action, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { update } from 'serializr';
import { useStore } from '@vromo/mobx-react';

import NewDashboardUserForm from './forms/NewDashboardUserForm';
import NewTeamForm from './forms/NewTeamForm';
import SubTeamExplorer from './SubTeamExplorer';
import NewDriverForm from './forms/NewDriverForm';
import EditSelectedTeamForm from './forms/EditSelectedTeamForm';
import Team from './Team';

import { css } from 'emotion';

import Button from '@vromo/ui/components/Button';
import Container from '@vromo/ui/components/Container';
import Modal from '@vromo/ui/components/Modal';
import Header from '@vromo/ui/components/Header';
import Tracking from '@vromo/analytics';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import theme from '@vromo/ui/components/theme';
import { useModals } from '@vromo/ui/components/Modals';
import { RootStore } from '../stores/RootStore';
import { EntityList } from '@vromo/models';
import { Role } from '@vromo/models';
import { Group } from '@vromo/models';
import Input from '@vromo/ui/components/Input';
import uniqBy from 'lodash/uniqBy';
import { debounce } from 'lodash';
import newEntityListFrom from '@vromo/models/helpers';
import {qaLabelTeams}from './../../../../common-qa-labels/teams';
import { useTranslation } from 'react-i18next';

type Props = {
    roles: EntityList<Role>;
};

export type GroupTree = {
    group: Group;
    expanded: boolean;
    children: Array<GroupTree>;
};

export default observer(function TeamEditor({ roles }: Props) {
    const rootStore = useStore<RootStore>('rootStore');
    const { groupStore, vehicleStore } = rootStore;
    const { graph, role } = rootStore.authStore;

    const [selectedGroup, setSelectedGroup] = useState<Group>();
    const [parent, setParent] = useState<Group>();
    const [groupTree, setGroupTree] = useState<GroupTree>();
    const [filteredGroupTree, setFilteredGroupTree] = useState<GroupTree>();
    const [parents, setParents] = useState<Group[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    const [driverModal, setDriverModal] = useState(false);
    const [inviteModal, setInviteModal] = useState(false);
    const [teamModal, setTeamModal] = useState(false);
    const [editModal, setEditModal] = useState(false);

    const modals = useModals();
    const { t } = useTranslation();

    useEffect(() => {
        return autorun(() => {
            const { childDictionary } = groupStore;
            let rootGroup = groupStore.groups.first();
            childDictionary.forEach((children, id) => {
                if (rootGroup?.id) {
                    const currentRootChildren = childDictionary.get(rootGroup.id);

                    if (currentRootChildren && currentRootChildren.length < children.length) {
                        rootGroup = groupStore.withId(id);
                    }
                }
            });
            if (rootGroup) {
                setSelectedGroup(rootGroup);
                setParent(rootGroup);

                const tree = constructGroupTree(rootGroup);

                setGroupTree(tree);
                setFilteredGroupTree(tree);
            }
        });
    }, []);

    const constructGroupTree = (group: Group): GroupTree => {
        const children = groupStore.getChildren(group);
        const directChildren = uniqBy(
            children.filter((c) => c.parents?.some((p) => p.id === group.id)),
            (g) => g.id
        );

        return {
            group,
            expanded: group.id === parent?.id,
            children: directChildren.map((c) => constructGroupTree(c))
        };
    };

    const openDriverModal = () => setDriverModal(true);
    const closeDriverModal = () => setDriverModal(false);

    const openInviteModal = () => setInviteModal(true);
    const closeInviteModal = () => setInviteModal(false);

    const openNewTeamModal = () => setTeamModal(true);
    const closeNewTeamModal = () => setTeamModal(false);

    const openTeamEditModal = () => setEditModal(true);
    const closeTeamEditModal = () => setEditModal(false);

    const editTeamName = (name: string) => {
        const editedGroup = newEntityListFrom<Group>(Group, [{ id: selectedGroup!.id, name }]);

        graph.root
            .graph()
            .roles(role.id)
            .groups()
            .post(editedGroup)
            .then(
                action(({ items }) => {
                    update(groupStore.groups, { items, type: 'update' });
                })
            );

        closeTeamEditModal();
    };

    const containsTerm = useCallback(
        (group: Group, term: string) => {
            if (group.name?.toLowerCase().includes(term)) return true;
            const teamRolesWithMatch = roles.filter((role) => {
                return (
                    role.account?.id === group.id &&
                    role.user?.displayName?.toLowerCase().includes(term)
                );
            });
            if (teamRolesWithMatch.length > 0) return true;
            const teamVehiclesWithMatch = vehicleStore.withGroup(group.id!).filter((vehicle) => {
                return vehicle.driver?.displayName?.toLowerCase().includes(term);
            });
            return teamVehiclesWithMatch.length > 0;
        },
        [roles, vehicleStore]
    );

    const filterGroupTree = useCallback(
        (tree: GroupTree, searchTerm: string): GroupTree => {
            const findNode = (node: GroupTree): boolean => {
                return (
                    containsTerm(node.group, searchTerm) ||
                    (node.children.length > 0 && !!node.children.find((child) => findNode(child)))
                );
            };

            const filtered = tree.children
                .filter((c) => findNode(c))
                .map((c) => filterGroupTree(c, searchTerm));

            return {
                ...tree,
                ...{
                    expanded: searchTerm.length > 0 && filtered.length > 0,
                    children: filtered
                }
            };
        },
        [containsTerm]
    );

    const handleChangeSearchTerm = (e: ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = e.target.value?.toLowerCase();
        setSearchTerm(newSearchTerm);
    };

    const clearSearchTerm = () => setSearchTerm('');

    const filter = useCallback(() => {
        if (groupTree) {
            const filtered = filterGroupTree(groupTree, searchTerm);
            setFilteredGroupTree(filtered);
        }
    }, [filterGroupTree, groupTree, searchTerm]);

    const delayedFilter = useMemo(() => debounce(filter, 500), [filter]);

    useEffect(() => {
        if (searchTerm) delayedFilter();
        else filter();
        return delayedFilter.cancel;
    }, [delayedFilter, filter, searchTerm]);

    function followBreadcrumb(group: Group) {
        setSelectedGroup(group);
    }

    function navigate(g: Group) {
        setSelectedGroup(g);
    }

    async function deleteTeam(group: Group) {
        if (
            await modals.confirm(
                t('teams.preventDelete'),
                t('teams.confirmDeleteGroup', { groupName: group.name })
            )
        ) {
            graph.root
                .graph()
                .roles(role.id)
                .groups()
                .delete({ _all_id__in: group.id })
                .then(() => {
                    followBreadcrumb(groupStore.groups.find((it) => it.id === group.parent)!);

                    runInAction(() => {
                        update(groupStore.groups, { items: [{ id: group.id }], type: 'delete' });
                    });

                    Tracking.track('Group Deleted', group);
                });
        }
    }

    function renderBreadcrumbs() {
        return (
            <>
                {parents.map((it) => (
                    <div style={{ display: 'flex' }} key={it.id}>
                        <a key={it.id} onClick={() => followBreadcrumb(it)}>
                            <Header>{it.name}</Header>
                        </a>
                        <FontAwesome icon="angle-right" style={{ margin: '7px 7px 0px 7px' }} />
                    </div>
                ))}
                <Header>{selectedGroup ? selectedGroup.name : ''}</Header>
            </>
        );
    }

    return (
        <div style={{ padding: 8 }}>
            <Container>
                <div
                    className={css`
                        display: flex;
                        align-items: middle;
                        flex-direction: row;

                        button {
                            margin-left: 10px;
                        }

                        @media (max-width: 500px) {
                            flex-direction: column;

                            button {
                                margin: 5px 0px;
                            }
                        }
                    `}>
                    {renderBreadcrumbs()}
                    <div
                        className={css`
                            display: flex;
                            flex: 1;
                            justify-content: flex-end;

                            @media (max-width: 500px) {
                                flex-direction: column;
                            }
                        `}>
                        <Button
                            data-testid ={qaLabelTeams.editTeamButton}
                            style={{ backgroundColor: theme.primary }}
                            onClick={openTeamEditModal}>
                            {t('teams.editTeam')}
                        </Button>
                        <Button data-testid ={qaLabelTeams.newTeamButton} onClick={openNewTeamModal}>{t('teams.newTeam')}</Button>
                        <Button data-testid ={qaLabelTeams.newDriverButton} onClick={openDriverModal}>{t('teams.newDriver')}</Button>
                        <Button data-testid ={qaLabelTeams.newDashboardUserButton} onClick={openInviteModal}>{t('teams.newDashboardUser')}</Button>
                    </div>
                </div>
            </Container>
            <div
                className={css`
                    display: flex;
                    flex-direction: row;
                    align-items: flex-start;

                    @media (max-width: 500px) {
                        flex-direction: column;
                    }
                `}>
                <Container style={{ maxHeight: 800, overflowY: 'auto' }} testId="group-container">
                    <div>
                        {groupStore.groups.length > 10 && (
                            <Input
                                placeholder={t('teams.searchTeamPlaceholder')}
                                style={{ marginBottom: 10 }}
                                onChange={handleChangeSearchTerm}
                                value={searchTerm}
                                onClearClick={clearSearchTerm}
                            />
                        )}
                        {filteredGroupTree?.group?.id && (
                            <SubTeamExplorer
                                key={filteredGroupTree?.group?.id}
                                groupTree={filteredGroupTree}
                                depth={0}
                                selected={selectedGroup?.id}
                                navigate={navigate}
                            />
                        )}
                    </div>
                </Container>
                <Container style={{ flex: 5 }}>
                    {selectedGroup?.id && (
                        <Team
                            group={selectedGroup}
                            key={selectedGroup.id}
                            roles={roles}
                            searchTerm={searchTerm}
                            vehicles={vehicleStore.withGroup(selectedGroup.id)}
                        />
                    )}

                    <Modal isOpen={editModal} onRequestClose={closeTeamEditModal}>
                        <EditSelectedTeamForm
                            name={selectedGroup?.name ?? ''}
                            save={editTeamName}
                            close={closeTeamEditModal}
                        />
                    </Modal>
                    <Modal isOpen={teamModal} onRequestClose={closeNewTeamModal}>
                        <NewTeamForm close={closeNewTeamModal} />
                    </Modal>
                    <Modal isOpen={driverModal} onRequestClose={closeDriverModal}>
                        <NewDriverForm close={closeDriverModal} />
                    </Modal>
                    <Modal isOpen={inviteModal} onRequestClose={closeInviteModal}>
                        <NewDashboardUserForm roles={roles} close={closeInviteModal} />
                    </Modal>
                </Container>
            </div>
        </div>
    );
});
